import {useMemo} from 'react';

import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useSelector} from 'react-redux';

import {ENDPOINTS} from 'constants/endpoints';
import {useDebounceValue} from 'hooks/useDebounce';
import {apiConfig} from 'libs/api/api';

import {getBillingAddress} from '../../store/selectors/billingAddress/billingAddress';
import {getShippingAddress} from '../../store/selectors/shippingAddress/shippingAddress';
import {emailValidationSchema} from '../../utils/deliveryFormValidation/deliveryFormValidation';

/**
 * Check shipping email with kickbox api, suggest if there is typo
 * Example: email@gnail.com
 * Suggestion: email@gmail.con
 */
export const useEmailSuggestion = (target: 'shippingAddress' | 'billingAddress') => {
    const {email} = useSelector(
        target === 'shippingAddress' ? getShippingAddress : getBillingAddress,
    );

    const debouncedEmail = useDebounceValue(email?.value, 300);

    const {success: isValidEmail} = useMemo(
        () => emailValidationSchema.safeParse(debouncedEmail || ''),
        [debouncedEmail],
    );
    const queryClient = useQueryClient();
    const queryKey = ['emailSuggestion', debouncedEmail, ENDPOINTS.VERIFY_EMAIL, apiConfig.API_URL];

    const {data: emailSuggestion} = useQuery({
        enabled: isValidEmail,
        queryFn: () =>
            apiConfig.instanceGusaBackend.request<{
                didYouMean?: string | null;
                reason: string;
                result: string;
            }>({
                baseURL: apiConfig.API_URL,
                data: {
                    email: debouncedEmail,
                },
                method: 'post',
                url: ENDPOINTS.VERIFY_EMAIL,
            }),
        queryKey,
        staleTime: Infinity,
    });

    return {
        emailSuggestion: emailSuggestion?.data?.didYouMean,
        reason: emailSuggestion?.data.reason,
        resetSuggestion: () => queryClient.setQueryData(queryKey, null),
        result: emailSuggestion?.data.result,
    };
};
